<template>
  <div id="chart-wind">
    <highcharts v-if="activeChart" :options="chartOptions" :callback="onLoad"></highcharts>
  </div>
</template>

<script>
import {createNamespacedHelpers} from "vuex";
import {Chart} from 'highcharts-vue'
import moment from "moment-timezone";

// Store
const {
  mapState: mapStateOfLiveMap,
  mapMutations: mapMutationsOfLiveMap
} = createNamespacedHelpers("liveMap");

export default {
  components: {
    highcharts: Chart
  },
  props: {
    windData: null,
  },
  data() {
    return {
      chart: null,
      activeChart: false,
      windDirection: null,
      windDirectionPathIds: [],
      oceanWindIcon: require("@/assets/images/weather/ocean_wind.svg"),
      chartOptions: {
        chart: {
          plotBorderWidth: 1,
          height: 400,
          alignTicks: false,
          scrollablePlotArea: {
            minWidth: 2000
          },
          spacingTop: 40,
          spacingRight: 80,
          spacingBottom: 50,
        },
        legend: {
          enabled: false
        },
        title: {
          text: null
        },
        tooltip: {
          outside: false,
          shared: false,
          useHTML: true,
          borderColor: '#2D9CDB',
          formatter: function () {
            let time = moment(this.x).format("MM/DD(dd) HH:mm")
            let windSpeed = `：${this.y}m/s`
            return '<p><b>' + time + '</b></p><p>' + this.series.name + '<b>' + windSpeed + '</b></p>'
          },
          style: {
            fontSize: '12px'
          }
        },
        series: [{
          name: '風速',
          pointStart: 0,
          pointInterval: 3600 * 1000,
          data: [],
          color: "#2F80ED"
        },
        ],
        xAxis: [
          {
            crosshair: {
              color: "#D5EBF8",
              width: 30,
              className: "offing-wave-point-detail-crosshair"
            },
            type: 'datetime',
            startOnTick: false,
            endOnTick: false,
            maxPadding: 0,
            minPadding: 0,
            tickInterval: 3600 * 1000,
            offset: 30,
            showLastLabel: false,
            tickLength: -30,
            labels: {
              useHTML: true,
              formatter: function () {
                let time = moment(this.value).format('HH')
                return `<p>${time}</p>`
              },
            },
            gridLineWidth: 0
          },
          {
            linkedTo: 0,
            type: 'datetime',
            tickInterval: 3600 * 1000,
            startOnTick: false,
            endOnTick: false,
            maxPadding: 0,
            minPadding: 0,
            labels: {
              overflow: "allow",
              autoRotation: false,
              align: "Left",
              formatter: function () {
                let startOfDay = moment(this.value).startOf('days')
                if (startOfDay.isSame(moment(this.value))) return moment(this.value).format('M/D');
              },
              style: {
                fontSize: '12px'
              },
              x: -5,
              y: 330,
            },
            opposite: true,
            tickLength: 0,
            gridLineWidth: 1
          }],
        yAxis: [{
          title: {
            text: "<p>風速<p/><p>(m/s)<p/>",
            rotation: 0,
            offset: 50,
            y: -80,
            useHTML: true,
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            }
          },
          min: 0,
          max: 20,
          labels: {
            formatter: function () {
              return Number(this.value).toFixed(1)
            },
            style: {
              fontSize: '10px',
              fontWeight: '500'
            },
            x: -10
          },
          maxPadding: 0.3,
          minRange: 8,
          tickInterval: 2.5,
          gridLineColor: 'rgba(128, 128, 128, 0.1)'
        }],
      }
    };
  },
  watch: {
    windData(data) {
      if (data) {
        this.windDirection = data.windDirection
        let initialTime = moment(data.initialTime, 'YYYY-MM-DD HH:mm')
        if (!this.chart) {
          this.chartOptions.series[0].pointStart = initialTime.valueOf()
          this.chartOptions.series[0].data = data.windSpeed
          this.activeChart = true
        } else {
          if (this.windDirectionPathIds.length !== 0) {
            this.windDirectionPathIds.forEach((id) => {
              let dom = document.getElementById(id);
              if(dom) {
                dom.remove()
              }
            })
            this.windDirectionPathIds = []
          }
          this.chart.series[0].pointStart = initialTime.valueOf()
          this.chart.series[0].setData(data.windSpeed, true, true)
          this.chart.xAxis[0].setExtremes(initialTime.valueOf(), initialTime.add(3, "d").valueOf(), true)
          this.chart.xAxis[1].setExtremes(initialTime.valueOf(), initialTime.add(3, "d").valueOf(), true)
          this.renderWindDirectionIcon()
        }
      }
    }
  },
  computed: {
    ...mapStateOfLiveMap({})
  },
  methods: {
    ...mapMutationsOfLiveMap([]),
    show() {
      this.activeChart = true
    },
    onLoad(chart) {
      this.chart = chart
      this.renderWindDirectionIcon()
    },
    renderWindDirectionIcon() {
      let xAxis = this.chart.xAxis[0],
        x,
        pos,
        max,
        isLast,
        i;
      for (pos = xAxis.min, max = moment(xAxis.min).add(3, "d"), i = 0; pos <= max + 36e5; pos += 36e5, i += 1) {
        isLast = pos === max + 36e5;
        x = Math.round(xAxis.toPixels(pos)) + (isLast ? 0.5 : -0.5);
        let pathId = `wind-direction-path-${this.windDirection[i - 1]}`
        this.windDirectionPathIds.push(pathId)
        this.chart.renderer
          .path([
              "M", 5.7741, 0.369188,
              "C", 5.37404, -0.11818, 4.62827, -0.11818, 4.22821, 0.369189,
              "L", 0.228955, 5.24126,
              "C", -0.306778, 5.89392, 0.157528, 6.87574, 1.0019, 6.87574,
              "H", 2.80195,
              "L", 2.80195, 20,
              "C", 2.80195, 20.5523, 3.24966, 21.0001, 3.80195, 21.0001,
              "H", 6.19773,
              "C", 6.75002, 21.0001, 7.19773, 20.5523, 7.19773, 20,
              "L", 7.19773, 6.87574,
              "L", 9.00042, 6.87574,
              "C", 9.84479, 6.87574, 10.3091, 5.89392, 9.77336, 5.24126,
              "L", 5.7741, 0.369188,
              "Z"
            ]
          )
          .attr({
            translateX: x + 6,
            translateY: this.chart.plotTop + this.chart.plotHeight + 4,
            rotation: this.windDirection[i] + 180,
            rotationOriginX: 5,
            rotationOriginY: 12,
            fill: "#2D9CDB",
            id: pathId
          })
          .add();
      }
    }
  },
  mounted() {},
  created() {},
};
</script>

<style lang="scss">
</style>
